import { Skeleton } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ScreenBody } from '../../../core-components/theme/components/Screen';
import type { LoadState } from '../../../core-components/theme/components/Screen';
import { SurfaceCard } from '../../../core-components/theme/components/SurfaceCard';
import { SETTINGS_SKELETON_ROWS, skeletonCardSx, skeletonRowSx } from '../constants/layout';
import { stepNoticeOf } from '../domain/steps';
import type { StepGate } from '../domain/steps';

import type { StepFrameProps } from './StepFrame';

const SETTINGS_SKELETON = (
  <SurfaceCard aria-hidden sx={skeletonCardSx}>
    {Array.from({ length: SETTINGS_SKELETON_ROWS }, (_, row) => (
      <Skeleton key={row} variant="rounded" sx={skeletonRowSx} />
    ))}
  </SurfaceCard>
);

export interface SettingsGateProps {
  readonly load: LoadState;
  readonly children: React.ReactNode;
}

export const SettingsGate: React.FC<SettingsGateProps> = ({ load, children }) => {
  const { t } = useTranslation();
  return (
    <ScreenBody
      load={load}
      errorTitle={t('closeTheLoop.settings.errorTitle', 'Couldn’t load your alert settings')}
      loadingLabel={t('closeTheLoop.settings.loading', 'Loading your alert settings')}
      loadingFallback={SETTINGS_SKELETON}
    >
      {children}
    </ScreenBody>
  );
};

export type GatedDock = Pick<StepFrameProps, 'primary' | 'secondary' | 'tertiary' | 'notice' | 'onBack'>;

export const gatedDockOf = (t: TFunction, gate: StepGate, next: () => void, ready: GatedDock): GatedDock => {
  switch (gate) {
    case 'loading':
      return {
        primary: { label: t('onboarding.continue', 'Continue'), onPress: next, unavailable: true },
        notice: stepNoticeOf({ loading: t('onboarding.loadingSettings', 'Loading your alert settings…') }),
      };
    case 'failed':
      return { tertiary: { label: t('onboarding.skipStep', 'Skip this step'), onPress: next } };
    case 'ready':
      return ready;
  }
};

